/**
 * Lazy Chatwoot SDK bootstrap
 *
 * Fetches widget config, injects `sdk.js` once per page load, and waits for `chatwoot:ready`.
 * Called only when the user opens support chat, not on app mount.
 */
import { ChatwootWidgetRuntimeConfig } from '@physiofit-digital/physiofit_types';
import { fetchChatwootWidgetRuntimeConfig } from './fetchChatwootWidgetRuntimeConfig';

const SCRIPT_ID = 'chatwoot-onboarding-sdk-script';

let initPromise: Promise<ChatwootWidgetRuntimeConfig | null> | null = null;

function injectWidgetScript(cfg: ChatwootWidgetRuntimeConfig): void {
  if (typeof window === 'undefined') return;
  if (window.chatwootSDK || document.getElementById(SCRIPT_ID)) return;

  window.chatwootSettings = {
    hideMessageBubble: true,
    position: 'right',
    locale: 'de',
    type: 'standard',
  };

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = `${cfg.baseUrl}/packs/js/sdk.js`;
  script.async = true;
  script.onload = () => {
    window.chatwootSDK?.run({ websiteToken: cfg.websiteToken, baseUrl: cfg.baseUrl });
  };
  document.head.appendChild(script);
}

function waitForChatwootReady(): Promise<void> {
  if (window.$chatwoot) return Promise.resolve();
  return new Promise((resolve) => {
    const onReady = () => {
      window.removeEventListener('chatwoot:ready', onReady);
      resolve();
    };
    window.addEventListener('chatwoot:ready', onReady);
  });
}

/**
 * Ensures the Chatwoot SDK is loaded and ready (deduped via module `initPromise`).
 *
 * @return {Promise<Object | null>} Resolved widget config or null on failure.
 */
export function ensureOnboardingChatwoot(): Promise<ChatwootWidgetRuntimeConfig | null> {
  if (!initPromise) {
    initPromise = (async () => {
      const cfg = await fetchChatwootWidgetRuntimeConfig();
      if (!cfg) return null;
      injectWidgetScript(cfg);
      await waitForChatwootReady();
      return cfg;
    })();
  }
  return initPromise;
}
